import { spError, spErrorTags, spLogTags, spWarnTags } from "@eventr/common";
import { StateCreator } from "zustand";
import { DataAPI } from "../../data/Data";
import { isChatAvailable } from "../../feat/chatpool/chat-eligibility";
import EmbeddedChatApi from "../../feat/rocketchat/chatBody/EmbeddedChatApi";
import { getRocketChatUrl, getRocketChatWebsocket } from "../../utils";
import { BoundStore } from "../bound.store";
import { getLiveToken, useRocketchatUiStore } from "../rocketchatState/rocketchatUi.store";
import { canOpenWebsocket } from "./canOpenWebsocket";
import { RocketSocketStatus } from "./rocketchat-utils";
import { RocketChannel, RocketchatSlice } from "./rocketchat.interface";
import { isSocketConnectingOrOpen } from "./socket-state";
import {
  cancelReconnect,
  currentSocketGeneration,
  isCurrentSocketGeneration,
  nextSocketGeneration,
  noteSocketActivity,
  scheduleReconnect,
  stopLivenessWatchdog,
} from "./socket-supervisor";

/** What the current socket was opened for, so a repeat openWebsocket() can keep it. */
let openedSocket: { socket?: WebSocket; url?: string; authToken?: string } = {};

export const createRocketchatSlice: StateCreator<BoundStore, [["zustand/devtools", never]], [], RocketchatSlice> = (
  set,
  get,
) => ({
  pushedTokens: [],
  rocketchannels: {} as Record<number, RocketChannel[]>,
  rocketchannelsPending: {},
  rocketWebsocket: null,
  rocketSocketStatus: null,
  chatNotificationPreference: null,
  pushToken: undefined,
  setPushToken: (token: string) => {
    set({ pushToken: token });
  },
  pullRocketChatChannels: async () => {
    const { selectedItinID } = get();
    if (selectedItinID === -1) return;

    set({ rocketchannelsPending: { ...get().rocketchannelsPending, [selectedItinID]: true } });

    try {
      const channels = await DataAPI.rocketchat.getChannels(selectedItinID);
      const { rocketchannels } = get();

      if (channels.status === "success") {
        set({
          rocketchannels: {
            ...rocketchannels,
            [selectedItinID]: channels.data ?? [],
          },
        });
      } else if (!(selectedItinID in rocketchannels)) {
        // Settle the itinerary: key presence is what marks it fetched.
        spErrorTags("Failed to pull chat channels", ["rocketchat", "ERROR"], selectedItinID);
        set({
          rocketchannels: {
            ...rocketchannels,
            [selectedItinID]: [],
          },
        });
      }
    } finally {
      const pending = { ...get().rocketchannelsPending };
      delete pending[selectedItinID];
      set({ rocketchannelsPending: pending });
    }
  },
  getDirectChannel: async (user_id: number, user_id2: number) => {
    const { rocketchannels } = get();
    const directChannelList =
      Object.values(rocketchannels)
        .flat()
        .find(
          (channel) =>
            channel.type === "direct" && channel?.members?.includes(user_id) && channel?.members?.includes(user_id2),
        ) ?? null;
    return directChannelList;
  },
  openWebsocket: () => {
    const { rocketWebsocket, selectedOrg, orgConfig, closeWebsocket } = get();
    cancelReconnect();
    try {
      if (isSocketConnectingOrOpen(rocketWebsocket)) {
        // Several paths ask for a socket at start-up and on resume (token check, app resume,
        // the chat screen, getRooms). One already connecting or open to the same server with
        // the same session is the socket they want: replacing it dropped every subscription
        // and showed as a close (1005) and reconnect on every load. A new token or org still
        // differs here and reconnects.
        const liveToken = getLiveToken();
        if (
          selectedOrg &&
          liveToken &&
          openedSocket.socket === rocketWebsocket &&
          openedSocket.url === getRocketChatWebsocket(selectedOrg.name) &&
          openedSocket.authToken === liveToken.authToken
        ) {
          return;
        }
        closeWebsocket(true, "rocketchat.slice, openWebsocket, with retry socket");
      }

      const openCheck = canOpenWebsocket(isChatAvailable(orgConfig));
      if (!openCheck.canOpen || !selectedOrg) {
        if (openCheck.retryable) {
          scheduleReconnect(openCheck.reason);
        }
        return;
      }

      const liveToken = getLiveToken();
      if (liveToken) {
        useRocketchatUiStore
          .getState()
          .setRocketchatBaseApi(
            new EmbeddedChatApi(getRocketChatUrl(liveToken.org), liveToken?.userId, liveToken.authToken),
          );
      }
      const rcUrl = getRocketChatWebsocket(selectedOrg.name);
      // Handlers of a superseded socket are ignored, so a late close cannot overwrite the
      // status of the socket that replaced it.
      const socketGeneration = nextSocketGeneration();
      const websocket = new WebSocket(rcUrl);
      set({ rocketSocketStatus: RocketSocketStatus.CONNECTING });
      // Separate from the provider's onmessage, so the liveness signal survives whatever
      // that handler is currently set to.
      websocket.addEventListener("message", () => {
        if (!isCurrentSocketGeneration(socketGeneration)) return;
        noteSocketActivity();
      });
      websocket.onerror = (event) => {
        if (!isCurrentSocketGeneration(socketGeneration)) return;
        set({ rocketSocketStatus: RocketSocketStatus.ERROR });
        spErrorTags("Websocket error", ["rocketchat"], event);
      };
      websocket.onopen = () => {
        if (!isCurrentSocketGeneration(socketGeneration)) return;
        spWarnTags("Websocket opened at url " + rcUrl, ["rocketchat"]);
        set({ rocketSocketStatus: RocketSocketStatus.OPEN });
        noteSocketActivity();
        try {
          websocket.send(
            JSON.stringify({
              msg: "connect",
              version: "1",
              support: ["1"],
            }),
          );
        } catch (error) {
          spError(error);
        }
      };
      websocket.onclose = (event) => {
        if (!isCurrentSocketGeneration(socketGeneration)) return;
        spWarnTags("Websocket closed " + event.code, ["rocketchat"]);
        stopLivenessWatchdog();
        set({ rocketSocketStatus: RocketSocketStatus.NONE });
        scheduleReconnect("websocket closed " + event.code);
      };
      set({ rocketWebsocket: websocket });
      openedSocket = { socket: websocket, url: rcUrl, authToken: liveToken?.authToken };
    } catch (error) {
      spErrorTags("Error opening websocket", ["rocketchat"], error);
      scheduleReconnect("websocket open threw");
    }
  },
  closeWebsocket: (noRestart?: boolean, traceId?: string) => {
    spLogTags("Closing websocket" + (traceId ? ", trace:" + traceId : ""), ["rocketchat"]);
    const { rocketWebsocket } = get();
    stopLivenessWatchdog();
    if (noRestart) {
      cancelReconnect();
    }
    try {
      if (rocketWebsocket) {
        if (noRestart) {
          const closingGeneration = currentSocketGeneration();
          rocketWebsocket.onclose = (e) => {
            spWarnTags("Websocket closed " + e.code, ["rocketchat"]);
            if (!isCurrentSocketGeneration(closingGeneration)) return;
            set({ rocketSocketStatus: RocketSocketStatus.NONE });
          };
        }
        if (isSocketConnectingOrOpen(rocketWebsocket)) {
          rocketWebsocket.close();
          spLogTags("Websocket closed", ["rocketchat"]);
        } else {
          spLogTags("Websocket is not open, with try closing anyway", ["rocketchat"]);
          try {
            rocketWebsocket.close();
          } catch (error) {
            spErrorTags("Failed closing websocket", ["rocketchat"], error);
          }
        }
      }
    } catch (error) {
      spErrorTags("Error closing websocket", ["rocketchat"], error);
    }
    try {
      useRocketchatUiStore.getState().rocketchatBaseApi?.close();
      spLogTags("Rocketchat base api closed", ["rocketchat"]);
    } catch (error) {
      spErrorTags("Error closing rocketchat base api", ["rocketchat"], error);
    }
  },
});

//MARK : SELECTORS

// A shared frozen empty array, so the miss case returns a stable reference.
//
// zustand 5 dropped useSyncExternalStoreWithSelector in favour of calling React's
// useSyncExternalStore directly, which means the selector now runs on every getSnapshot
// with no memoisation in front of it. A literal `?? []` therefore hands React a new array
// identity every call, and React reports "The result of getSnapshot should be cached to
// avoid an infinite loop". Under zustand 4 the same code was harmless, so this was latent
// long before the upgrade.
const NO_CHANNELS: RocketChannel[] = [];

export const selectRocketChatChannels = (state: BoundStore) => {
  const { selectedItinID, rocketchannels } = state;
  return rocketchannels[selectedItinID] ?? NO_CHANNELS;
};
